/**
 * Pure helpers for the dynamic-tree file/folder picker.
 *
 * Lives in its own module so the picker-variant routing and item-filter
 * logic can be unit-tested without booting the full picker component
 * (which depends on the connection store, the dynamic-tree hook, and
 * Fluent's Popover).
 */

import type { TreeItem } from '@/hooks/useDynamicTree';
import type { DynamicTreeSettings } from '@/types/operationParameter';

/**
 * Two picker variants are supported, mirroring LogicAppsUX semantics:
 *
 *  - `'folder'` (`canSelectParentNodes && !canSelectLeafNodes`): only
 *    folders are surfaced. Clicking a folder commits it; navigation is
 *    via an explicit `>` chevron button.
 *  - `'file'` (default for anything else): files and folders are both
 *    shown. Clicking a file commits it; clicking a folder navigates.
 */
export type PickerType = 'folder' | 'file';

/** Derive the picker variant from the connector's `x-ms-dynamic-tree.settings`. */
export function derivePickerType(settings: DynamicTreeSettings | undefined): PickerType {
  return settings?.canSelectParentNodes && settings?.canSelectLeafNodes === false
    ? 'folder'
    : 'file';
}

/** Folder pickers only ever surface folders; file pickers show everything. */
export function filterVisibleItems(items: TreeItem[], pickerType: PickerType): TreeItem[] {
  return pickerType === 'folder' ? items.filter((item) => item.isFolder) : items;
}

/**
 * A row is interactive when clicking it does *something*: folders always
 * navigate (or commit, in folder pickers), and files commit only in file
 * pickers when the swagger marks them selectable. Non-interactive rows
 * render dimmed and skip keyboard handlers.
 */
export function isItemInteractive(item: TreeItem, pickerType: PickerType): boolean {
  return !!(item.isFolder || (pickerType === 'file' && item.isSelectable));
}
